import React from "react";
import { useFormContext } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/molecules/Form";
import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandProps,
} from "./Command";

type CommandInputProps = React.ComponentProps<typeof CommandInput> & {
  errorMsg?: string;
};

export type CommandFieldProps = CommandProps & {
  name: string;
  inputClassName?: string;
  label?: React.ReactNode;
  subcopy?: React.ReactNode;
  note?: React.ReactNode;
  labelClassName?: string;
  /** Applied to the inner cmdk root */
  commandClassName?: string;
};

const CommandField = React.forwardRef<HTMLDivElement, CommandFieldProps>(
  (
    {
      className,
      label,
      subcopy,
      note,
      name,
      fullWidth = false,
      children,
      onValueChange,
      onOpenChange,
      labelClassName,
      commandClassName,
      ...commandProps
    },
    ref,
  ) => {
    const { control } = useFormContext();

    return (
      <FormField
        control={control}
        name={name}
        render={({ field, fieldState }) => {
          return (
            <FormItem
              ref={ref}
              disabled={commandProps.disabled}
              readOnly={commandProps.readOnly}
              className={cn({ "tw-w-full tw-flex-1": fullWidth }, className)}
            >
              {!!label && (
                <FormLabel subcopy={subcopy} className={labelClassName}>
                  {label}
                </FormLabel>
              )}
              <FormControl forwardErrorMsg>
                <Command
                  className={commandClassName}
                  onValueChange={onValueChange}
                  onOpenChange={onOpenChange}
                >
                  {React.Children.map(children, (child) => {
                    if (
                      React.isValidElement(child) &&
                      child.type === CommandInput
                    ) {
                      return React.cloneElement(
                        child as React.ReactElement<CommandInputProps>,
                        {
                          errorMsg: fieldState?.error?.message,
                          ...field,
                        },
                      );
                    }
                    return child;
                  })}
                </Command>
              </FormControl>
              <FormMessage>{note}</FormMessage>
            </FormItem>
          );
        }}
      />
    );
  },
);
export {
  CommandEmpty,
  CommandField,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
};
CommandField.displayName = "CommandField";
